/**
 * SIDEBAR NAVIGATION - the grouped nav config.
 *
 * Moved out of mockData.js (8/28/26). This decides what an admin can reach and it was sitting
 * between fake insulin orders and a list of made-up sales reps; nothing about the items changed in
 * the move except the grouping below.
 *
 * GROUPS EXIST TO BREAK UP A LONG LIST, and two rules follow from that (both enforced in
 * Sidebar.js, both tested):
 *   1. a group whose every item is hidden for this user renders NOTHING - header included;
 *   2. under GROUPED_MIN_ITEMS visible items the list renders FLAT, headers dropped, because four
 *      headers over a five-item list cost more height than the items do.
 * Rule 2 is why a sales_rep still gets a plain list. Note that ROLE NEVER APPEARS HERE - only the
 * count does - so a role added later gets sensible treatment without anyone touching layout code.
 *
 * CONTENT and DELETION REQUESTS ARE DELIBERATELY ABSENT. They are administered a few times a year
 * and were each holding a top-level slot; they are tabs on Settings now (see SettingsTabs.js).
 * They kept their own top-level admin-guarded routes - that file explains why nesting them under
 * /settings would have been a security regression.
 */
export const NAV_GROUPS = [
  {
    label: 'Operations',
    items: [
      { label: 'Overview', icon: 'dashboard', path: '/distributor', masterOnly: true },
      { label: 'Customers', icon: 'home_health', path: '/distributor/customers', masterOnly: true },
      { label: 'Orders', icon: 'order_approve', path: '/distributor/orders', masterOnly: true },
      { label: 'IVRs', icon: 'call_quality', path: '/distributor/ivrs', masterOnly: true },
      { label: 'Shipments', icon: 'package_2', path: '/distributor/shipments', masterOnly: true },
    ],
  },
  {
    label: 'Business',
    items: [
      {
        label: 'Virtual Tissue Bank',
        icon: 'account_balance',
        path: '/distributor/bank',
        masterOnly: true,
      },
      { label: 'Reports', icon: 'finance_mode', path: '/distributor/reports', masterOnly: true },
    ],
  },
  {
    label: 'Tools & support',
    items: [
      {
        label: 'Resources & Tools',
        icon: 'folder_open',
        path: '/distributor/resources',
        masterOnly: true,
      },
      { label: 'Help Center', icon: 'support_agent', path: '/distributor/help', masterOnly: true },
      { label: 'Settings', icon: 'settings', path: '/distributor/settings', masterOnly: true },
    ],
  },
  {
    label: 'Work',
    items: [
      { label: 'Dashboard', icon: 'dashboard', path: '/' },
      { label: 'Orders', icon: 'order_approve', path: '/orders' },
      { label: 'IVR Details', icon: 'call_quality', path: '/ivr-details' },
      { label: 'Shipments', icon: 'package_2', path: '/shipments' },
      {
        label: 'Shipments',
        icon: 'package_2',
        path: '/distributor-clinic/shipments',
        distributorClinicOnly: true,
      },
    ],
  },
  {
    label: 'Billing',
    items: [
      { label: 'Invoices', icon: 'receipt_long', path: '/invoices' },
      // Admin-only by construction: '/statements' is absent from REP_ALLOWED_PATHS, so the route
      // is wrapped in RequireAdmin and Sidebar hides what a rep cannot reach.
      { label: 'Statements', icon: 'description', path: '/statements' },
      { label: 'Reports', icon: 'finance_mode', path: '/reports' },
    ],
  },
  {
    label: 'Records',
    items: [
      { label: 'Sales Reps', icon: 'analytics', path: '/sales-reps' },
      // repOnly: a rep's own clinics (ordering switch, product allow-list, credit) plus their
      // roster of granted reps. An admin reaches the same controls from the Clinics tab and grants
      // rep access on Sales Reps, so the item would be a dead end for them. Replaced My Team
      // (9/4/26); /my-team redirects here.
      { label: 'My Hub', icon: 'hub', path: '/my-hub', repOnly: true },
      { label: 'Clinics', icon: 'home_health', path: '/clinics' },
      { label: 'Products', icon: 'inventory_2', path: '/products' },
      { label: 'Users', icon: 'group', path: '/users' },
    ],
  },
  {
    label: 'Distributor',
    items: [
      { label: 'Distributor Customers', icon: 'home_health', path: '/admin/distributor-customers' },
      { label: 'Distributor Orders', icon: 'order_approve', path: '/admin/distributor-orders' },
      { label: 'Distributor IVRs', icon: 'call_quality', path: '/admin/distributor-ivrs' },
      { label: 'Distributor Shipments', icon: 'package_2', path: '/admin/distributor-shipments' },
      {
        label: 'Distributor Tissue Bank',
        icon: 'account_balance',
        path: '/admin/distributor-bank',
      },
      {
        label: 'Distributor Resources & Tools',
        icon: 'folder_open',
        path: '/admin/distributor-resources',
      },
      { label: 'Distributor Setup', icon: 'domain_add', path: '/admin/distributors' },
      { label: 'Distributor Reports', icon: 'finance_mode', path: '/admin/distributor-reports' },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Integrations', icon: 'link', path: '/integrations' },
      { label: 'Audit Logs', icon: 'shield', path: '/audit-logs' },
      { label: 'Settings', icon: 'settings', path: '/settings' },
      { label: 'Resources & Tools', icon: 'folder_open', path: '/resources' },
      { label: 'Help Center', icon: 'support_agent', path: '/help-center' },
    ],
  },
];

// The separate administrator workspace keeps the distributor's established grouping and labels.
// Ordinary MedEffects administrators retain the prefixed Distributor section above.
export const DISTRIBUTOR_ADMIN_GROUPS = [
  {
    label: 'Operations',
    items: [
      { label: 'Overview', icon: 'dashboard', path: '/admin/distributor-overview' },
      { label: 'Customers', icon: 'home_health', path: '/admin/distributor-customers' },
      { label: 'Orders', icon: 'order_approve', path: '/admin/distributor-orders' },
      { label: 'IVRs', icon: 'call_quality', path: '/admin/distributor-ivrs' },
      { label: 'Shipments', icon: 'package_2', path: '/admin/distributor-shipments' },
    ],
  },
  {
    label: 'Business',
    items: [
      { label: 'Virtual Tissue Bank', icon: 'account_balance', path: '/admin/distributor-bank' },
      { label: 'Reports', icon: 'finance_mode', path: '/admin/distributor-reports' },
      { label: 'Distributor Setup', icon: 'domain_add', path: '/admin/distributors' },
    ],
  },
  {
    label: 'Tools & support',
    items: [
      { label: 'Resources & Tools', icon: 'folder_open', path: '/admin/distributor-resources' },
      { label: 'Settings', icon: 'settings', path: '/settings' },
    ],
  },
];

/** Below this many VISIBLE items the sidebar drops the headers and renders one flat list. */
export const GROUPED_MIN_ITEMS = 8;

/** Every item, ungrouped - for tests and anything that just wants the set of nav paths. */
export const sidebarItems = NAV_GROUPS.flatMap((group) => group.items);
